import type {
  ScheduleEventCommunicationPresentation,
  WorkOrderPlanningContactOrderSummary,
} from "../../../../../../../../../../../../../api/kehrblatt/work-orders"
import { formatAppointmentHeadingWindow } from "../../../../../../../../../../../../../_shared/_logic/appointment-time-tags"
import { buildScheduleEventCommunicationTagPresentation } from "../../../../../../../../../../../../../_shared/_logic/communication-presentation"
import { displayScheduleStatusLabel } from "../../../../../../../../../../../../../_shared/_logic/status-definitions"

export const ANNOUNCEMENT_PLAN_EMPTY_TEXT = "Noch kein Ankündigungsplan angelegt"
export const CANCELED_ANNOUNCEMENT_PLAN_EMPTY_TEXT =
  "Für diesen stornierten Termin gab es keinen Ankündigungsplan."

export type AppointmentAnnouncementOrder = Pick<WorkOrderPlanningContactOrderSummary, "id"> &
  Partial<
    Pick<
      WorkOrderPlanningContactOrderSummary,
      "schedule_event_id" | "status" | "proposed_start_at" | "created_at" | "updated_at"
    >
  >

export type AppointmentAnnouncementSection = {
  id: string
  title: string
  subtitle: string
  description: string
  emptyText: string
  event: ScheduleEvent | null
  orders: AppointmentAnnouncementOrder[]
  sortTs: number
  activityTs: number
  displayNumber: number
  isCurrent: boolean
}

type EmployeeOption = { id: string; label: string }

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i

const parseTimestamp = (value?: string | null): number => {
  if (!value) return 0

  const parsed = new Date(value).getTime()

  return Number.isNaN(parsed) ? 0 : parsed
}

const maxTimestamp = (values: Array<unknown>): number =>
  values.reduce<number>((latest, value) => {
    const timestamp = typeof value === "string" ? parseTimestamp(value) : 0

    return timestamp > latest ? timestamp : latest
  }, 0)

const contactOrderActivityTimestamp = (order: AppointmentAnnouncementOrder): number => {
  const rawOrder = order as unknown as Record<string, unknown>

  return maxTimestamp([
    rawOrder.closed_at,
    rawOrder.completed_at,
    rawOrder.canceled_at,
    rawOrder.cancelled_at,
    rawOrder.updated_at,
    rawOrder.created_at,
  ])
}

const eventActivityTimestamp = (
  event: ScheduleEvent,
  orders: AppointmentAnnouncementOrder[],
): number => {
  const rawEvent = event as unknown as Record<string, unknown>
  const lifecycle = (event.lifecycle_summary ?? {}) as Record<string, unknown>
  const activity = maxTimestamp([
    lifecycle.latest_occurred_at,
    lifecycle.canceled_at,
    lifecycle.cancelled_at,
    lifecycle.completed_at,
    lifecycle.confirmed_at,
    lifecycle.noshow_at,
    rawEvent.canceled_at,
    rawEvent.cancelled_at,
    rawEvent.completed_at,
    rawEvent.confirmed_at,
    rawEvent.noshow_at,
    rawEvent.updated_at,
    rawEvent.created_at,
    ...orders.map(contactOrderActivityTimestamp),
  ])

  return activity || parseTimestamp(event.start_at)
}

const formatDateTime = (value?: string | null) => {
  if (!value) return "Zeit offen"

  const parsed = new Date(value)

  if (Number.isNaN(parsed.getTime())) return "Zeit offen"

  return `${parsed.toLocaleDateString("de-DE")} ${parsed.toLocaleTimeString("de-DE", {
    hour: "2-digit",
    minute: "2-digit",
  })}`
}

const formatEventTitleTime = (event: ScheduleEvent) => {
  return formatAppointmentHeadingWindow(event) ?? formatDateTime(event.start_at)
}

const formatEventStatusSubtitle = (
  event: ScheduleEvent,
  communicationPresentationsByScheduleEventId: Record<
    string,
    ScheduleEventCommunicationPresentation
  >,
) => {
  const label = buildScheduleEventCommunicationTagPresentation({
    status: event.status,
    event,
    presentation: communicationPresentationsByScheduleEventId[event.id],
  }).label

  return label
}

const normalizeId = (value: unknown) => {
  if (value === null || value === undefined) return null

  const normalized = String(value).trim()

  return normalized || null
}

const isUuidLike = (value: string | null) => Boolean(value && UUID_PATTERN.test(value))

export const announcementEventIsCanceled = (event?: ScheduleEvent | null) => {
  if (!event) return false

  const status = String(event.status || "")
    .trim()
    .toUpperCase()

  if (["CANCELED", "CANCELLED"].includes(status)) return true

  if (["RESERVED", "ANNOUNCED", "CONFIRMED", "COMPLETED"].includes(status)) return false

  return Boolean(event.lifecycle_summary?.canceled_at)
}

const emptyTextForEvent = (event?: ScheduleEvent | null) =>
  announcementEventIsCanceled(event)
    ? CANCELED_ANNOUNCEMENT_PLAN_EMPTY_TEXT
    : ANNOUNCEMENT_PLAN_EMPTY_TEXT

export const resolveContactOrderScheduleEventId = (
  order: AppointmentAnnouncementOrder,
  relevantScheduleEventId?: string | null,
) => normalizeId(order.schedule_event_id) ?? normalizeId(relevantScheduleEventId)

const resolveDirectContactOrderScheduleEventId = (order: AppointmentAnnouncementOrder) =>
  normalizeId(order.schedule_event_id)

const eventBelongsToWorkOrder = ({
  event,
  workOrderId,
  linkedEventIds,
  relevantEventIds,
}: {
  event: ScheduleEvent
  workOrderId: string | null
  linkedEventIds: Set<string>
  relevantEventIds: Set<string>
}) => {
  const eventId = normalizeId(event.id)

  if (!eventId) return false

  const eventWorkOrderId = normalizeId((event as any)?.work_order_id)

  if (workOrderId && eventWorkOrderId) return eventWorkOrderId === workOrderId

  if (!workOrderId) return linkedEventIds.has(eventId) || relevantEventIds.has(eventId)

  return linkedEventIds.has(eventId) || relevantEventIds.has(eventId)
}

const describeScheduleEvent = (
  event: ScheduleEvent,
  isCurrent: boolean,
  employeeLabelById: Map<string, string>,
) => {
  const status = displayScheduleStatusLabel(event.status)
  const employeeId = normalizeId(event.employee_id)
  const employee =
    normalizeId(event.employee_name) ??
    (employeeId ? normalizeId(employeeLabelById.get(employeeId)) : null) ??
    "nicht zugewiesen"
  const canceledAt =
    normalizeId(event.lifecycle_summary?.canceled_at) ?? normalizeId((event as any)?.canceled_at)

  if (announcementEventIsCanceled(event) && canceledAt) {
    const canceledByLabel =
      normalizeId(event.lifecycle_summary?.canceled_by_label) ??
      normalizeId((event as any)?.canceled_by_label)
    const canceledByUserId =
      normalizeId(event.lifecycle_summary?.canceled_by_user_id) ??
      normalizeId((event as any)?.canceled_by_user_id)
    const canceledBy =
      (canceledByLabel && !isUuidLike(canceledByLabel) ? canceledByLabel : null) ??
      (canceledByUserId ? normalizeId(employeeLabelById.get(canceledByUserId)) : null)

    return `Abgesagt am ${formatDateTime(canceledAt)}${
      canceledBy ? ` von ${canceledBy}` : ""
    }. Mitarbeiter: ${employee}.`
  }

  const prefix = isCurrent ? "Aktueller Termin" : "Termin"

  return `${prefix} im Status ${status}. Mitarbeiter: ${employee}.`
}

const buildVirtualOrderSection = (
  order: AppointmentAnnouncementOrder,
  index: number,
): AppointmentAnnouncementSection => {
  const start = order.proposed_start_at

  return {
    id: `contact-order-${order.id}`,
    title: formatDateTime(start),
    subtitle: "Terminvorschlag ohne Kalendereintrag",
    description: "Für diesen Kundenkontakt ist kein Kalendereintrag verknüpft.",
    emptyText: ANNOUNCEMENT_PLAN_EMPTY_TEXT,
    event: null,
    orders: [order],
    sortTs: parseTimestamp(start) || index,
    activityTs: contactOrderActivityTimestamp(order) || parseTimestamp(start) || index,
    displayNumber: 0,
    isCurrent: false,
  }
}

export const buildAppointmentAnnouncementSections = ({
  workOrder,
  scheduleEvents = [],
  allScheduleEvents = [],
  contactOrders = [],
  relevantScheduleEvent = null,
  relevantScheduleEventId = null,
  employees = [],
  communicationPresentationsByScheduleEventId = {},
}: {
  workOrder?: WorkOrder | null
  scheduleEvents?: ScheduleEvent[]
  allScheduleEvents?: ScheduleEvent[]
  contactOrders?: AppointmentAnnouncementOrder[]
  relevantScheduleEvent?: ScheduleEvent | null
  relevantScheduleEventId?: string | null
  employees?: EmployeeOption[]
  communicationPresentationsByScheduleEventId?: Record<
    string,
    ScheduleEventCommunicationPresentation
  >
}): AppointmentAnnouncementSection[] => {
  const eventsById = new Map<string, ScheduleEvent>()
  const employeeLabelById = new Map(
    (employees ?? [])
      .map((employee) => [normalizeId(employee.id), normalizeId(employee.label)] as const)
      .filter((entry): entry is [string, string] => Boolean(entry[0] && entry[1])),
  )
  const workOrderId = normalizeId(workOrder?.id)
  const linkedEventIds = new Set(
    contactOrders
      .map(resolveDirectContactOrderScheduleEventId)
      .filter((id): id is string => Boolean(id)),
  )
  const relevantEventIds = new Set(
    [normalizeId(relevantScheduleEventId), normalizeId(relevantScheduleEvent?.id)].filter(
      (id): id is string => Boolean(id),
    ),
  )
  const addEvent = (event?: ScheduleEvent | null) => {
    if (!event?.id) return

    if (
      !eventBelongsToWorkOrder({
        event,
        workOrderId,
        linkedEventIds,
        relevantEventIds,
      })
    ) {
      return
    }

    eventsById.set(event.id, event)
  }
  scheduleEvents.forEach(addEvent)
  allScheduleEvents.forEach(addEvent)
  addEvent(relevantScheduleEvent)

  const ordersByEventId = new Map<string, AppointmentAnnouncementOrder[]>()
  const unassignedOrders: AppointmentAnnouncementOrder[] = []
  contactOrders.forEach((order) => {
    const scheduleEventId = resolveContactOrderScheduleEventId(order, relevantScheduleEventId)

    if (scheduleEventId && eventsById.has(scheduleEventId)) {
      const list = ordersByEventId.get(scheduleEventId) ?? []
      list.push(order)
      ordersByEventId.set(scheduleEventId, list)

      return
    }

    unassignedOrders.push(order)
  })

  const currentId = normalizeId(relevantScheduleEventId) ?? normalizeId(relevantScheduleEvent?.id)
  const eventSections = Array.from(eventsById.values()).map((event) => {
    const isCurrent = Boolean(currentId && event.id === currentId)
    const orders = ordersByEventId.get(event.id) ?? []

    return {
      id: `event-${event.id}`,
      title: formatEventTitleTime(event),
      subtitle: formatEventStatusSubtitle(event, communicationPresentationsByScheduleEventId),
      description: describeScheduleEvent(event, isCurrent, employeeLabelById),
      emptyText: emptyTextForEvent(event),
      event,
      orders,
      sortTs: parseTimestamp(event.start_at),
      activityTs: eventActivityTimestamp(event, orders),
      displayNumber: 0,
      isCurrent,
    } satisfies AppointmentAnnouncementSection
  })

  const virtualSections = unassignedOrders.map(buildVirtualOrderSection)
  const sections = [...eventSections, ...virtualSections]

  return [...sections]
    .sort((left, right) => {
      if (left.activityTs !== right.activityTs) return right.activityTs - left.activityTs

      if (left.sortTs !== right.sortTs) return right.sortTs - left.sortTs

      return left.id.localeCompare(right.id)
    })
    .map((section, index) => ({
      ...section,
      displayNumber: sections.length - index,
    }))
}
